/**
 * Page Name Mapping Utility
 *
 * Maps route paths to human-readable page names and categories
 * for activity tracking and analytics.
 */

// Static route mappings (exact matches)
const STATIC_ROUTES = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/login': 'Login',
  '/join': 'Join Team',
  '/no-access': 'No Access',
  '/complete-profile': 'Complete Profile',
  '/projects': 'Projects',
  '/flux': 'Flux',
  '/ems': 'Flux EMS',
  '/phd': 'Flux PhD',
  '/demo': 'Eddi Demo',
  '/profile': 'Profile',
  '/dev/deal-analysis': 'Deal Analysis (Dev)',

  // Admin routes
  '/admin': 'Admin Dashboard',
  '/admin/team': 'Team & Seats',
  '/admin/company': 'Company Settings',
  '/admin/companies': 'All Companies',
  '/admin/users': 'All Users',
  '/admin/subscriptions': 'Subscriptions',
  '/admin/settings': 'Settings',
  '/admin/billing': 'Billing',
  '/admin/tariffs': 'Tariff Management',
  '/admin/equipment': 'Equipment Library',
  '/admin/solver': 'Solver Monitor',
  '/admin/capex': 'CapEx & OpEx',
  '/admin/der-charges': 'CA DER Charges',
  '/admin/extractions': 'Extraction Review',
  '/admin/bugs': 'Bug Reports',
  '/admin/tariff-approval': 'Tariff Approval',
  '/admin/load-profiles': 'Load Profiles',
  '/admin/budgets': 'Budget Templates',
};

// Dynamic route patterns (matched with regex)
const DYNAMIC_ROUTES = [
  {
    pattern: /^\/projects\/([^/]+)\/scenarios\/new$/,
    getName: (matches) => 'New Scenario',
    category: 'projects',
  },
  {
    pattern: /^\/projects\/([^/]+)\/scenarios\/([^/]+)$/,
    getName: (matches) => 'Edit Scenario',
    category: 'projects',
  },
  {
    pattern: /^\/projects\/([^/]+)$/,
    getName: (matches) => 'Project Analysis',
    category: 'projects',
  },
  {
    pattern: /^\/phd\/simulation\/([^/]+)$/,
    getName: (matches) => 'PhD Simulation',
    category: 'flux_phd',
  },
];

// Category mappings
const CATEGORY_MAPPINGS = {
  '/dashboard': 'dashboard',
  '/login': 'auth',
  '/join': 'auth',
  '/no-access': 'auth',
  '/complete-profile': 'auth',
  '/projects': 'projects',
  '/flux': 'flux_ems',
  '/ems': 'flux_ems',
  '/phd': 'flux_phd',
  '/demo': 'demo',
  '/profile': 'profile',
  '/dev': 'dev',
  '/admin': 'admin',
};

/**
 * Get a human-readable page name for a given route path.
 *
 * @param {string} path - The route path (e.g., '/projects/123')
 * @returns {string} Human-readable page name (e.g., 'Project Analysis')
 */
export function getPageName(path) {
  if (!path || typeof path !== 'string') {
    return 'Unknown Page';
  }

  // Normalize path - remove trailing slash and query params
  const normalizedPath = path.split('?')[0].replace(/\/$/, '') || '/';

  // Check static routes first
  if (STATIC_ROUTES[normalizedPath]) {
    return STATIC_ROUTES[normalizedPath];
  }

  // Check dynamic routes
  for (const route of DYNAMIC_ROUTES) {
    const matches = normalizedPath.match(route.pattern);
    if (matches) {
      return route.getName(matches);
    }
  }

  // Fallback: generate name from path
  return generateNameFromPath(normalizedPath);
}

/**
 * Get the category for a given route path.
 *
 * Categories help group related pages for analytics:
 * - admin: Administrative pages
 * - projects: Site DNA projects
 * - flux_ems: Flux EMS monitoring
 * - flux_phd: Flux PhD simulation
 * - auth: Authentication pages
 * - dashboard: Main dashboard
 * - profile: User profile
 * - demo: Demo pages
 * - dev: Development/test pages
 *
 * @param {string} path - The route path
 * @returns {string} Category name
 */
export function getPageCategory(path) {
  if (!path || typeof path !== 'string') {
    return 'unknown';
  }

  // Normalize path
  const normalizedPath = path.split('?')[0].replace(/\/$/, '') || '/';

  // Check dynamic routes first (they have explicit categories)
  for (const route of DYNAMIC_ROUTES) {
    if (route.pattern.test(normalizedPath)) {
      return route.category;
    }
  }

  // Check category mappings by prefix
  for (const [prefix, category] of Object.entries(CATEGORY_MAPPINGS)) {
    if (normalizedPath === prefix || normalizedPath.startsWith(prefix + '/')) {
      return category;
    }
  }

  // Root path
  if (normalizedPath === '/') {
    return 'dashboard';
  }

  return 'other';
}

/**
 * Generate a human-readable name from a path segment.
 *
 * @param {string} path - The route path
 * @returns {string} Generated page name
 */
function generateNameFromPath(path) {
  // Get the last meaningful segment
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) {
    return 'Home';
  }

  const lastSegment = segments[segments.length - 1];

  // Skip ID-like segments (UUIDs, numeric IDs)
  const isIdLike = /^[0-9a-f-]{20,}$/i.test(lastSegment) || /^\d+$/.test(lastSegment);

  if (isIdLike && segments.length > 1) {
    // Use the second-to-last segment and add "Detail"
    const parentSegment = segments[segments.length - 2];
    return formatSegment(parentSegment) + ' Detail';
  }

  return formatSegment(lastSegment);
}

/**
 * Format a path segment as a human-readable name.
 *
 * @param {string} segment - Path segment (e.g., 'tariff-approval')
 * @returns {string} Formatted name (e.g., 'Tariff Approval')
 */
function formatSegment(segment) {
  return segment
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default {
  getPageName,
  getPageCategory,
};
